import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  TableContainer,
  Tabs,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Edit3,
  KeyRound,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  UserX,
} from "lucide-react";

import { useAuth } from "../../auth/AuthContext";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import {
  ALL_USER_ROLES,
  formatUserRole,
  normalizeUserRole,
  USER_ROLES,
} from "./constants/userRoles";
import {
  AdminAddress,
  AdminAuditRecord,
  AdminMember,
  AdminUserRecord,
  AdminWarehouse,
  adminUsersService,
} from "./services/adminUsers.service";
import UserSearchDropdown, {
  SearchableUser,
} from "../settings/UserSearchDropdown";

const cardsBorderRadii = "8px";
const USER_STATUSES = ["active", "suspended", "pending", "deleted"] as const;
type UserStatus = (typeof USER_STATUSES)[number];

const toInputValue = (value: unknown) =>
  value === null || value === undefined ? "" : String(value);

const toNumber = (value: unknown, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatDate = (
  value: AdminUserRecord["createdAt"] | AdminAuditRecord["createdAt"],
) => {
  if (!value) return "—";

  if (typeof value === "object") {
    const seconds = value.seconds ?? value._seconds;
    if (seconds) return new Date(seconds * 1000).toLocaleString();
  }

  const date = new Date(value as string | number);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const roleColor = (role?: string) => {
  switch (normalizeUserRole(role)) {
    case USER_ROLES.SUPER_ADMIN:
      return "red";
    case USER_ROLES.ADMIN:
      return "purple";
    case USER_ROLES.MANAGER:
      return "blue";
    case USER_ROLES.STAFF:
      return "cyan";
    case USER_ROLES.SHOP:
      return "teal";
    case USER_ROLES.USER:
      return "green";
    default:
      return "gray";
  }
};

const statusColor = (status?: string) => {
  switch (String(status || "active").toLowerCase()) {
    case "active":
      return "green";
    case "suspended":
      return "red";
    case "pending":
      return "orange";
    case "deleted":
      return "gray";
    default:
      return "gray";
  }
};

const UsersAdminPage = () => {
  const { user: currentFirebaseUser } = useAuth();
  const { theme } = useCustomTheme();
  const toast = useToast();
  const detailsDrawer = useDisclosure();
  const deleteDialog = useDisclosure();
  const cancelDeleteRef = useRef<HTMLButtonElement | null>(null);

  const [users, setUsers] = useState<AdminUserRecord[]>([]);
  const [selectedUser, setSelectedUser] = useState<AdminUserRecord | null>(
    null,
  );
  const [editForm, setEditForm] = useState<AdminUserRecord | null>(null);
  const [members, setMembers] = useState<AdminMember[]>([]);
  const [addresses, setAddresses] = useState<AdminAddress[]>([]);
  const [warehouses, setWarehouses] = useState<AdminWarehouse[]>([]);
  const [auditLogs, setAuditLogs] = useState<AdminAuditRecord[]>([]);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [savingSection, setSavingSection] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [pointsAmount, setPointsAmount] = useState(0);
  const [pointsReason, setPointsReason] = useState("");
  const [resetLink, setResetLink] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [permanentDelete, setPermanentDelete] = useState(false);

  const [newMember, setNewMember] = useState<AdminMember | null>(null);

  const pageBg = theme.background;
  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const borderColor = theme.border;
  const borderStrong = theme.borderStrong;
  const inputBg = theme.inputBg;
  const inputBorder = theme.inputBorder;
  const subtleText = theme.textMuted;


  const loadUsers = useCallback(async () => {
    setLoading(true);

    try {
      const response = await adminUsersService.listUsers({
        search,
        role: roleFilter,
        status: statusFilter,
        page,
        limit,
      });

      setUsers(response.users);
      setTotal(response.total);
      setTotalPages(Math.max(response.totalPages, 1));
    } catch (error) {
      console.error("Failed to load users:", error);
      toast({
        title: "Failed to load users",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setLoading(false);
    }
  }, [limit, page, roleFilter, search, statusFilter, toast]);

  useEffect(() => {
    const timer = window.setTimeout(loadUsers, 300);
    return () => window.clearTimeout(timer);
  }, [loadUsers]);

  useEffect(() => {
    setPage(1);
  }, [search, roleFilter, statusFilter]);

  const syncSelectedUser = useCallback((nextUser: AdminUserRecord) => {
    setSelectedUser(nextUser);
    setEditForm({ ...nextUser });
    setMembers(Array.isArray(nextUser.members) ? nextUser.members : []);
    setAddresses(Array.isArray(nextUser.addresses) ? nextUser.addresses : []);
    setWarehouses(
      Array.isArray(nextUser.warehouses) ? nextUser.warehouses : [],
    );
    setUsers((currentUsers) =>
      currentUsers.map((item) => (item.uid === nextUser.uid ? nextUser : item)),
    );
  }, []);

  const openUser = async (userRecord: AdminUserRecord) => {
    detailsDrawer.onOpen();
    setDetailsLoading(true);
    setReason("");
    setResetLink("");
    setPointsAmount(0);
    setPointsReason("");
    setAuditLogs([]);
    setNewMember(null);

    try {
      const response = await adminUsersService.getUser(userRecord.uid);
      syncSelectedUser(response.user);
    } catch (error) {
      console.error("Failed to load user:", error);
      toast({
        title: "Failed to load user",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
      detailsDrawer.onClose();
    } finally {
      setDetailsLoading(false);
    }
  };

  const setField = (key: keyof AdminUserRecord, value: unknown) => {
    setEditForm((current) =>
      current ? { ...current, [key]: value } : current,
    );
  };

  const requireReason = () => {
    if (reason.trim()) return true;

    toast({
      title: "Reason required",
      description: "Add an administrative reason before changing access.",
      status: "warning",
      duration: 3000,
    });
    return false;
  };

  const saveProfile = async () => {
    if (!selectedUser || !editForm) return;

    setSavingSection("profile");
    try {
      const response = await adminUsersService.updateUser(
        selectedUser.uid,
        {
          displayName: editForm.displayName,
          name: editForm.name,
          email: editForm.email,
          phone: editForm.phone,
          phoneNumber: editForm.phoneNumber,
          photoURL: editForm.photoURL,
          theme: editForm.theme,
        },
        reason || "Super Admin updated the user profile.",
      );

      syncSelectedUser(response.user);
      toast({ title: "Profile updated", status: "success", duration: 2200 });
    } catch (error) {
      toast({
        title: "Profile update failed",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setSavingSection(null);
    }
  };

  const saveBusiness = async () => {
    if (!selectedUser || !editForm) return;

    setSavingSection("business");
    try {
      const response = await adminUsersService.updateUser(
        selectedUser.uid,
        {
          businessName: editForm.businessName,
          businessAddress: editForm.businessAddress,
          businessPhone: editForm.businessPhone,
          businessPhoneNumber: editForm.businessPhoneNumber,
          whatsappNumber: editForm.whatsappNumber,
          logoURL: editForm.logoURL,
          fontColor: editForm.fontColor,
          accountName: editForm.accountName,
          accountBank: editForm.accountBank,
          accountNumber: editForm.accountNumber,
          accountBranch: editForm.accountBranch,
        },
        reason || "Super Admin updated business and payout details.",
      );

      syncSelectedUser(response.user);
      toast({
        title: "Business details updated",
        status: "success",
        duration: 2200,
      });
    } catch (error) {
      toast({
        title: "Business update failed",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setSavingSection(null);
    }
  };

  const saveRole = async () => {
    if (!selectedUser || !editForm) return;

    const nextRole = normalizeUserRole(editForm.role);

    setSavingSection("role");
    try {
      const response = await adminUsersService.updateRole(
        selectedUser.uid,
        nextRole,
      );
      syncSelectedUser(response.user);
      toast({ title: "Role updated", status: "success", duration: 2200 });
    } catch (error) {
      toast({
        title: "Role update failed",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setSavingSection(null);
    }
  };

  const saveStatus = async () => {
    if (!selectedUser || !editForm || !requireReason()) return;

    setSavingSection("status");
    try {
      const response = await adminUsersService.updateStatus(
        selectedUser.uid,
        String(editForm.status || "active") as UserStatus,
        reason.trim(),
      );
      syncSelectedUser(response.user);
      toast({
        title: "Account status updated",
        status: "success",
        duration: 2200,
      });
    } catch (error) {
      toast({
        title: "Status update failed",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setSavingSection(null);
    }
  };

  const adjustPoints = async () => {
    if (!selectedUser) return;

    if (!pointsAmount || !pointsReason.trim()) {
      toast({
        title: "Amount and reason required",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    setSavingSection("points");
    try {
      const response = await adminUsersService.adjustPoints(
        selectedUser.uid,
        pointsAmount,
        pointsReason.trim(),
      );
      syncSelectedUser(response.user);
      setPointsAmount(0);
      setPointsReason("");
      toast({
        title: "Dengu Points adjusted",
        status: "success",
        duration: 2200,
      });
    } catch (error) {
      toast({
        title: "Points adjustment failed",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setSavingSection(null);
    }
  };

  const selectMemberForAdd = (candidate: SearchableUser) => {
    const memberId = String(candidate.uid || candidate.id || "").trim();

    if (!memberId) {
      toast({
        title: "This user could not be selected",
        status: "warning",
        duration: 2500,
      });
      return;
    }

    if (members.some((member) => member.id === memberId)) {
      toast({
        title: "Member already added",
        description: "This user is already a member of the selected account.",
        status: "info",
        duration: 2800,
      });
      setNewMember(null);
      return;
    }

    const name = String(
      candidate.displayName || candidate.name || candidate.email || memberId,
    );

    setNewMember({
      id: memberId,
      name,
      displayName: name,
      email: String(candidate.email || ""),
      photoURL: candidate.photoURL,
      role: USER_ROLES.STAFF,
    });
  };

  const addMember = async () => {
    if (!selectedUser || !newMember?.id.trim()) {
      toast({
        title: "Search for and select a member first",
        status: "warning",
        duration: 2500,
      });
      return;
    }

    setSavingSection("member-add");
    try {
      const response = await adminUsersService.addMember(selectedUser.uid, {
        ...newMember,
        id: newMember.id.trim(),
        role: normalizeUserRole(newMember.role || USER_ROLES.STAFF),
      });

      syncSelectedUser(response.user);
      setNewMember(null);
      toast({ title: "Member added", status: "success", duration: 2200 });
    } catch (error) {
      toast({
        title: "Failed to add member",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setSavingSection(null);
    }
  };

  const updateMember = async (member: AdminMember) => {
    if (!selectedUser) return;

    setSavingSection(`member-${member.id}`);
    try {
      const response = await adminUsersService.updateMember(
        selectedUser.uid,
        member.id,
        member,
        reason || "Super Admin updated a business member.",
      );
      syncSelectedUser(response.user);
      toast({ title: "Member updated", status: "success", duration: 2000 });
    } catch (error) {
      toast({
        title: "Member update failed",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setSavingSection(null);
    }
  };

  const removeMember = async (memberId: string) => {
    if (!selectedUser) return;

    setSavingSection(`member-remove-${memberId}`);
    try {
      const response = await adminUsersService.removeMember(
        selectedUser.uid,
        memberId,
        reason || "Super Admin removed a business member.",
      );
      syncSelectedUser(response.user);
      toast({ title: "Member removed", status: "success", duration: 2000 });
    } catch (error) {
      toast({
        title: "Member removal failed",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setSavingSection(null);
    }
  };

  const saveAddress = async (address: AdminAddress) => {
    if (!selectedUser) return;

    setSavingSection(`address-${address.id}`);
    try {
      const response = await adminUsersService.updateAddress(
        selectedUser.uid,
        address.id,
        address,
        reason || "Super Admin updated an address.",
      );
      syncSelectedUser(response.user);
      toast({ title: "Address updated", status: "success", duration: 2000 });
    } catch (error) {
      toast({
        title: "Address update failed",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setSavingSection(null);
    }
  };

  const removeAddress = async (addressId: string) => {
    if (!selectedUser) return;

    setSavingSection(`address-remove-${addressId}`);
    try {
      const response = await adminUsersService.removeAddress(
        selectedUser.uid,
        addressId,
        reason || "Super Admin removed an address.",
      );
      syncSelectedUser(response.user);
      toast({ title: "Address removed", status: "success", duration: 2000 });
    } catch (error) {
      toast({
        title: "Address removal failed",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setSavingSection(null);
    }
  };

  const saveWarehouse = async (warehouse: AdminWarehouse) => {
    if (!selectedUser) return;

    setSavingSection(`warehouse-${warehouse.id}`);
    try {
      const response = await adminUsersService.updateWarehouse(
        selectedUser.uid,
        warehouse.id,
        warehouse,
        reason || "Super Admin updated a warehouse.",
      );
      syncSelectedUser(response.user);
      toast({ title: "Warehouse updated", status: "success", duration: 2000 });
    } catch (error) {
      toast({
        title: "Warehouse update failed",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setSavingSection(null);
    }
  };

  const removeWarehouse = async (warehouseId: string) => {
    if (!selectedUser) return;

    setSavingSection(`warehouse-remove-${warehouseId}`);
    try {
      const response = await adminUsersService.removeWarehouse(
        selectedUser.uid,
        warehouseId,
        reason || "Super Admin removed a warehouse.",
      );
      syncSelectedUser(response.user);
      toast({ title: "Warehouse removed", status: "success", duration: 2000 });
    } catch (error) {
      toast({
        title: "Warehouse removal failed",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setSavingSection(null);
    }
  };

  const generateResetLink = async () => {
    if (!selectedUser) return;

    setSavingSection("reset-link");
    try {
      const response = await adminUsersService.createPasswordResetLink(
        selectedUser.uid,
      );
      setResetLink(response.resetLink);
      toast({
        title: "Password reset link created",
        status: "success",
        duration: 2200,
      });
    } catch (error) {
      toast({
        title: "Could not create reset link",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setSavingSection(null);
    }
  };

  const loadAuditLogs = async () => {
    if (!selectedUser) return;

    setSavingSection("audit");
    try {
      const response = await adminUsersService.getAuditLogs(selectedUser.uid);
      setAuditLogs(response.logs);
    } catch (error) {
      toast({
        title: "Could not load audit history",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setSavingSection(null);
    }
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser || !deleteReason.trim()) {
      toast({
        title: "Deletion reason required",
        status: "warning",
        duration: 2500,
      });
      return;
    }

    setSavingSection("delete");
    try {
      await adminUsersService.deleteUser(
        selectedUser.uid,
        deleteReason.trim(),
        permanentDelete,
      );
      deleteDialog.onClose();
      detailsDrawer.onClose();
      setSelectedUser(null);
      setEditForm(null);
      setDeleteReason("");
      setPermanentDelete(false);
      await loadUsers();
      toast({
        title: permanentDelete
          ? "User permanently deleted"
          : "User soft deleted",
        status: "success",
        duration: 2500,
      });
    } catch (error) {
      toast({
        title: "User deletion failed",
        description:
          error instanceof Error ? error.message : "Please try again.",
        status: "error",
        duration: 3500,
      });
    } finally {
      setSavingSection(null);
    }
  };

  const summary = useMemo(
    () => ({
      superAdmins: users.filter(
        (item) => normalizeUserRole(item.role) === USER_ROLES.SUPER_ADMIN,
      ).length,
      admins: users.filter(
        (item) => normalizeUserRole(item.role) === USER_ROLES.ADMIN,
      ).length,
      suspended: users.filter(
        (item) =>
          String(item.status || "active") === "suspended" || item.disabled,
      ).length,
    }),
    [users],
  );

  return (
    <Box
      bg={pageBg}
      minH="100%"
      w="100%"
      maxW="100%"
      minW={0}
      p={{ base: 2, sm: 4, md: 6 }}
      color={theme.text}
      overflowX="hidden"
    >
      <VStack spacing={6} align="stretch">
        <Flex
          bg={cardBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius={cardsBorderRadii}
          boxShadow={theme.shadowMd}
          p={{ base: 4, md: 6 }}
          justify="space-between"
          align={{ base: "flex-start", lg: "center" }}
          direction={{ base: "column", lg: "row" }}
          gap={4}
        >
          <Box>
            <HStack spacing={2} mb={2} flexWrap="wrap">
              <Badge colorScheme="red" borderRadius={cardsBorderRadii} px={3} py={1}>
                Super Admin
              </Badge>
              <Badge borderRadius={cardsBorderRadii} px={3} py={1}>
                {total} users
              </Badge>
            </HStack>
            <Heading fontSize={{ base: "xl", sm: "2xl", md: "3xl" }}>
              User Management
            </Heading>
            <Text color={subtleText} mt={1} maxW="760px">
              Manage every PickDengu account, platform role, status, profile,
              business details, members, addresses, warehouses, points, and
              security actions from one protected page.
            </Text>
          </Box>

          <SimpleGrid
            columns={{ base: 1, sm: 3 }}
            spacing={3}
            w={{ base: "100%", lg: "auto" }}
          >
            <SummaryTile
              label="Super Admins"
              value={summary.superAdmins}
              theme={theme}
            />
            <SummaryTile label="Admins" value={summary.admins} theme={theme} />
            <SummaryTile
              label="Suspended"
              value={summary.suspended}
              theme={theme}
            />
          </SimpleGrid>
        </Flex>

        <Box
          bg={cardBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius={cardsBorderRadii}
          boxShadow={theme.shadowSm}
          p={{ base: 4, md: 5 }}
        >
          <Flex
            gap={3}
            direction={{ base: "column", lg: "row" }}
            align="stretch"
          >
            <InputGroup flex="1">
              <InputLeftElement pointerEvents="none">
                <Search size={16} />
              </InputLeftElement>
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search name, email, phone, UID, or business"
                bg={inputBg}
                borderColor={inputBorder}
              />
            </InputGroup>

            <Select
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
              maxW={{ base: "100%", lg: "190px" }}
              bg={inputBg}
              borderColor={inputBorder}
            >
              <option value="all">All roles</option>
              {ALL_USER_ROLES.map((role) => (
                <option key={role} value={role}>
                  {formatUserRole(role)}
                </option>
              ))}
            </Select>

            <Select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              maxW={{ base: "100%", lg: "190px" }}
              bg={inputBg}
              borderColor={inputBorder}
            >
              <option value="all">All statuses</option>
              {USER_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Select>

            <Button
              leftIcon={<RefreshCw size={16} />}
              onClick={loadUsers}
              variant="outline"
              borderColor={borderStrong}
              isLoading={loading}
            >
              Refresh
            </Button>
          </Flex>
        </Box>

        <Box
          bg={cardBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius={cardsBorderRadii}
          boxShadow={theme.shadowSm}
          overflow="hidden"
        >
          <Box display={{ base: "block", lg: "none" }}>
            {loading ? (
              <Flex py={12} justify="center" align="center">
                <Spinner />
              </Flex>
            ) : users.length === 0 ? (
              <Box py={12} px={4} textAlign="center">
                <Text color={subtleText}>
                  No users match the current filters.
                </Text>
              </Box>
            ) : (
              <VStack align="stretch" spacing={3} p={{ base: 3, sm: 4 }}>
                {users.map((userRecord) => {
                  const accountStatus = userRecord.disabled
                    ? "suspended"
                    : userRecord.status || "active";
                  const memberCount = Array.isArray(userRecord.members)
                    ? userRecord.members.length
                    : 0;

                  return (
                    <Box
                      key={userRecord.uid}
                      p={{ base: 3, sm: 4 }}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius={cardsBorderRadii}
                      bg={cardBg}
                      minW={0}
                    >
                      <Flex
                        justify="space-between"
                        align="flex-start"
                        gap={3}
                        mb={3}
                      >
                        <HStack spacing={3} minW={0} flex="1">
                          <Avatar
                            size="sm"
                            flexShrink={0}
                            src={userRecord.photoURL}
                            name={
                              userRecord.displayName ||
                              userRecord.name ||
                              userRecord.email
                            }
                          />
                          <Box minW={0} flex="1">
                            <Text fontWeight="800" noOfLines={1}>
                              {userRecord.displayName ||
                                userRecord.name ||
                                "Unnamed user"}
                            </Text>
                            <Text
                              fontSize="sm"
                              color={subtleText}
                              noOfLines={1}
                            >
                              {userRecord.email || "No email"}
                            </Text>
                          </Box>
                        </HStack>

                        <Tooltip label="Manage user">
                          <IconButton
                            aria-label="Manage user"
                            icon={<Edit3 size={16} />}
                            size="sm"
                            flexShrink={0}
                            onClick={() => openUser(userRecord)}
                            bg={theme.primary}
                            color={theme.textOnPrimary}
                            _hover={{ bg: theme.primaryHover }}
                          />
                        </Tooltip>
                      </Flex>

                      <Flex gap={2} flexWrap="wrap" mb={4}>
                        <Badge
                          colorScheme={roleColor(userRecord.role)}
                          textTransform="capitalize"
                          borderRadius={cardsBorderRadii}
                          px={2.5}
                          py={1}
                        >
                          {formatUserRole(userRecord.role)}
                        </Badge>
                        <Badge
                          colorScheme={statusColor(accountStatus)}
                          textTransform="capitalize"
                          borderRadius={cardsBorderRadii}
                          px={2.5}
                          py={1}
                        >
                          {accountStatus}
                        </Badge>
                      </Flex>

                      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                        <Box minW={0} flex="1">
                          <Text fontSize="xs" color={subtleText}>
                            Business
                          </Text>
                          <Text
                            fontSize="sm"
                            fontWeight="700"
                            wordBreak="break-word"
                          >
                            {userRecord.businessName || "—"}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={subtleText}>
                            Members
                          </Text>
                          <Text fontSize="sm" fontWeight="700">
                            {memberCount}
                          </Text>
                        </Box>
                        <Box minW={0}>
                          <Text fontSize="xs" color={subtleText}>
                            Created
                          </Text>
                          <Text
                            fontSize="sm"
                            fontWeight="700"
                            wordBreak="break-word"
                          >
                            {formatDate(userRecord.createdAt)}
                          </Text>
                        </Box>
                        <Box minW={0}>
                          <Text fontSize="xs" color={subtleText}>
                            User ID
                          </Text>
                          <Text
                            fontSize="xs"
                            color={subtleText}
                            wordBreak="break-all"
                          >
                            {userRecord.uid}
                          </Text>
                        </Box>
                      </SimpleGrid>

                      <Button
                        mt={4}
                        w="100%"
                        size="sm"
                        leftIcon={<Edit3 size={15} />}
                        onClick={() => openUser(userRecord)}
                        bg={theme.primary}
                        color={theme.textOnPrimary}
                        _hover={{ bg: theme.primaryHover }}
                      >
                        Manage User
                      </Button>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </Box>

          <TableContainer
            display={{ base: "none", lg: "block" }}
            overflowX="auto"
            sx={{ WebkitOverflowScrolling: "touch" }}
          >
            <Table variant="simple" minW="1050px">
              <Thead bg={softBg}>
                <Tr>
                  <Th>User</Th>
                  <Th>Role</Th>
                  <Th>Status</Th>
                  <Th>Business</Th>
                  <Th>Members</Th>
                  <Th>Created</Th>
                  <Th textAlign="right">Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <Tr>
                    <Td colSpan={7} py={12} textAlign="center">
                      <Spinner />
                    </Td>
                  </Tr>
                ) : users.length === 0 ? (
                  <Tr>
                    <Td colSpan={7} py={12} textAlign="center">
                      <Text color={subtleText}>
                        No users match the current filters.
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  users.map((userRecord) => (
                    <Tr key={userRecord.uid}>
                      <Td>
                        <HStack minW="260px">
                          <Avatar
                            size="sm"
                            src={userRecord.photoURL}
                            name={
                              userRecord.displayName ||
                              userRecord.name ||
                              userRecord.email
                            }
                          />
                          <Box minW={0}>
                            <Text fontWeight="700" noOfLines={1}>
                              {userRecord.displayName ||
                                userRecord.name ||
                                "Unnamed user"}
                            </Text>
                            <Text
                              fontSize="sm"
                              color={subtleText}
                              noOfLines={1}
                            >
                              {userRecord.email || userRecord.uid}
                            </Text>
                          </Box>
                        </HStack>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={roleColor(userRecord.role)}
                          textTransform="capitalize"
                        >
                          {formatUserRole(userRecord.role)}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={statusColor(
                            userRecord.disabled
                              ? "suspended"
                              : userRecord.status,
                          )}
                          textTransform="capitalize"
                        >
                          {userRecord.disabled
                            ? "suspended"
                            : userRecord.status || "active"}
                        </Badge>
                      </Td>
                      <Td>{userRecord.businessName || "—"}</Td>
                      <Td>
                        {Array.isArray(userRecord.members)
                          ? userRecord.members.length
                          : 0}
                      </Td>
                      <Td whiteSpace="nowrap">
                        {formatDate(userRecord.createdAt)}
                      </Td>
                      <Td textAlign="right">
                        <Tooltip label="Manage user">
                          <IconButton
                            aria-label="Manage user"
                            icon={<Edit3 size={16} />}
                            size="sm"
                            onClick={() => openUser(userRecord)}
                            bg={theme.primary}
                            color={theme.textOnPrimary}
                            _hover={{ bg: theme.primaryHover }}
                          />
                        </Tooltip>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>

          <Flex
            px={{ base: 3, sm: 5 }}
            py={4}
            borderTop="1px solid"
            borderColor={borderColor}
            justify="space-between"
            align={{ base: "stretch", sm: "center" }}
            direction={{ base: "column", sm: "row" }}
            gap={3}
          >
            <Text
              fontSize="sm"
              color={subtleText}
              textAlign={{ base: "center", sm: "left" }}
            >
              Page {page} of {totalPages} · {total} users
            </Text>
            <HStack justify={{ base: "center", sm: "flex-end" }}>
              <IconButton
                aria-label="Previous page"
                icon={<ChevronLeft size={17} />}
                size="sm"
                onClick={() => setPage((current) => Math.max(current - 1, 1))}
                isDisabled={page <= 1 || loading}
              />
              <IconButton
                aria-label="Next page"
                icon={<ChevronRight size={17} />}
                size="sm"
                onClick={() =>
                  setPage((current) => Math.min(current + 1, totalPages))
                }
                isDisabled={page >= totalPages || loading}
              />
            </HStack>
          </Flex>
        </Box>
      </VStack>

      <Drawer
        isOpen={detailsDrawer.isOpen}
        placement="right"
        size={{ base: "full", md: "xl" }}
        onClose={detailsDrawer.onClose}
      >
        <DrawerOverlay />
        <DrawerContent
          bg={cardBg}
          color={theme.text}
          maxW={{ base: "100vw", md: "xl" }}
        >
          <DrawerCloseButton />
          <DrawerHeader
            borderBottom="1px solid"
            borderColor={borderColor}
            px={{ base: 4, md: 6 }}
          >
            <HStack pr={10} minW={0}>
              <Avatar
                src={selectedUser?.photoURL}
                name={
                  selectedUser?.displayName ||
                  selectedUser?.name ||
                  selectedUser?.email
                }
              />
              <Box minW={0}>
                <Text noOfLines={1}>
                  {selectedUser?.displayName ||
                    selectedUser?.name ||
                    "User account"}
                </Text>
                <Text
                  fontSize="sm"
                  color={subtleText}
                  fontWeight="normal"
                  noOfLines={1}
                >
                  {selectedUser?.email || selectedUser?.uid}
                </Text>
              </Box>
            </HStack>
          </DrawerHeader>

          <DrawerBody p={0}>
            {detailsLoading || !editForm ? (
              <Flex minH="360px" align="center" justify="center">
                <Spinner />
              </Flex>
            ) : (
              <Tabs isLazy colorScheme="teal">
                <TabList
                  overflowX="auto"
                  overflowY="hidden"
                  px={{ base: 2, md: 4 }}
                  pt={3}
                  w="100%"
                  sx={{
                    WebkitOverflowScrolling: "touch",
                    "& > button": {
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      paddingInline: "12px",
                    },
                  }}
                >
                  <Tab>Profile</Tab>
                  <Tab>Access</Tab>
                  <Tab>Business</Tab>
                  <Tab>Members</Tab>
                  <Tab>Addresses</Tab>
                  <Tab>Warehouses</Tab>
                  <Tab>Points & Security</Tab>
                  <Tab onClick={loadAuditLogs}>Audit</Tab>
                </TabList>

                <TabPanels
                  w="100%"
                  minW={0}
                  sx={{ "& > div": { px: { base: 3, md: 4 } } }}
                >
                  <TabPanel>
                    <SectionTitle
                      title="Personal Profile"
                      description="Update the user's global account profile."
                    />
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <AdminInput
                        label="Display Name"
                        value={editForm.displayName}
                        onChange={(value) => setField("displayName", value)}
                        theme={theme}
                      />
                      <AdminInput
                        label="Name"
                        value={editForm.name}
                        onChange={(value) => setField("name", value)}
                        theme={theme}
                      />
                      <AdminInput
                        label="Email"
                        value={editForm.email}
                        onChange={(value) => setField("email", value)}
                        theme={theme}
                        type="email"
                      />
                      <AdminInput
                        label="Phone"
                        value={editForm.phone || editForm.phoneNumber}
                        onChange={(value) => {
                          setField("phone", value);
                          setField("phoneNumber", value);
                        }}
                        theme={theme}
                      />
                      <AdminInput
                        label="Photo URL"
                        value={editForm.photoURL}
                        onChange={(value) => setField("photoURL", value)}
                        theme={theme}
                      />
                      <FormControl>
                        <FormLabel color={subtleText}>Theme</FormLabel>
                        <Select
                          value={toInputValue(editForm.theme || "light")}
                          onChange={(event) =>
                            setField("theme", event.target.value)
                          }
                          bg={inputBg}
                          borderColor={inputBorder}
                        >
                          <option value="light">Light</option>
                          <option value="dark">Dark</option>
                        </Select>
                      </FormControl>
                      <AdminInput
                        label="Firebase UID"
                        value={selectedUser?.uid}
                        onChange={() => undefined}
                        theme={theme}
                        isReadOnly
                      />
                      <AdminInput
                        label="Created"
                        value={formatDate(editForm.createdAt)}
                        onChange={() => undefined}
                        theme={theme}
                        isReadOnly
                      />
                    </SimpleGrid>
                    <Button
                      mt={5}
                      w={{ base: "100%", sm: "auto" }}
                      onClick={saveProfile}
                      isLoading={savingSection === "profile"}
                      bg={theme.primary}
                      color={theme.textOnPrimary}
                      _hover={{ bg: theme.primaryHover }}
                    >
                      Save Profile
                    </Button>
                  </TabPanel>

                  <TabPanel>
                    <SectionTitle
                      title="Role and Account Status"
                      description="These changes affect platform-wide access and are always audited."
                    />
                    <VStack align="stretch" spacing={5}>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <FormControl>
                          <FormLabel color={subtleText}>
                            Platform Role
                          </FormLabel>
                          <Select
                            value={normalizeUserRole(editForm.role)}
                            onChange={(event) =>
                              setField("role", event.target.value)
                            }
                            bg={inputBg}
                            borderColor={inputBorder}
                          >
                            {ALL_USER_ROLES.map((role) => (
                              <option key={role} value={role}>
                                {formatUserRole(role)}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel color={subtleText}>
                            Account Status
                          </FormLabel>
                          <Select
                            value={toInputValue(
                              editForm.status ||
                                (editForm.disabled ? "suspended" : "active"),
                            )}
                            onChange={(event) =>
                              setField("status", event.target.value)
                            }
                            bg={inputBg}
                            borderColor={inputBorder}
                          >
                            {USER_STATUSES.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </SimpleGrid>

                      <FormControl isRequired>
                        <FormLabel color={subtleText}>
                          Administrative Reason
                        </FormLabel>
                        <Textarea
                          value={reason}
                          onChange={(event) => setReason(event.target.value)}
                          placeholder="Why is this access change required?"
                          bg={inputBg}
                          borderColor={inputBorder}
                        />
                      </FormControl>

                      <Flex direction={{ base: "column", sm: "row" }} gap={3}>
                        <Button
                          w={{ base: "100%", sm: "auto" }}
                          leftIcon={<ShieldCheck size={16} />}
                          onClick={saveRole}
                          isLoading={savingSection === "role"}
                          colorScheme="purple"
                        >
                          Update Role
                        </Button>
                        <Button
                          w={{ base: "100%", sm: "auto" }}
                          leftIcon={<UserX size={16} />}
                          onClick={saveStatus}
                          isLoading={savingSection === "status"}
                          colorScheme={
                            String(editForm.status) === "suspended"
                              ? "red"
                              : "orange"
                          }
                        >
                          Update Status
                        </Button>
                      </Flex>
                    </VStack>
                  </TabPanel>

                  <TabPanel>
                    <SectionTitle
                      title="Business and Banking"
                      description="Manage the user's business identity, contact information, branding, and payout details."
                    />
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <AdminInput
                        label="Business Name"
                        value={editForm.businessName}
                        onChange={(value) => setField("businessName", value)}
                        theme={theme}
                      />
                      <AdminInput
                        label="Business Address"
                        value={editForm.businessAddress}
                        onChange={(value) => setField("businessAddress", value)}
                        theme={theme}
                      />
                      <AdminInput
                        label="Business Phone"
                        value={
                          editForm.businessPhone || editForm.businessPhoneNumber
                        }
                        onChange={(value) => {
                          setField("businessPhone", value);
                          setField("businessPhoneNumber", value);
                        }}
                        theme={theme}
                      />
                      <AdminInput
                        label="WhatsApp Number"
                        value={editForm.whatsappNumber}
                        onChange={(value) => setField("whatsappNumber", value)}
                        theme={theme}
                      />
                      <AdminInput
                        label="Logo URL"
                        value={editForm.logoURL}
                        onChange={(value) => setField("logoURL", value)}
                        theme={theme}
                      />
                      <AdminInput
                        label="Font Color"
                        value={editForm.fontColor}
                        onChange={(value) => setField("fontColor", value)}
                        theme={theme}
                        type="color"
                      />
                      <AdminInput
                        label="Account Name"
                        value={editForm.accountName}
                        onChange={(value) => setField("accountName", value)}
                        theme={theme}
                      />
                      <AdminInput
                        label="Bank"
                        value={editForm.accountBank}
                        onChange={(value) => setField("accountBank", value)}
                        theme={theme}
                      />
                      <AdminInput
                        label="Account Number"
                        value={editForm.accountNumber}
                        onChange={(value) => setField("accountNumber", value)}
                        theme={theme}
                      />
                      <AdminInput
                        label="Branch"
                        value={editForm.accountBranch}
                        onChange={(value) => setField("accountBranch", value)}
                        theme={theme}
                      />
                    </SimpleGrid>
                    <Button
                      mt={5}
                      w={{ base: "100%", sm: "auto" }}
                      onClick={saveBusiness}
                      isLoading={savingSection === "business"}
                      bg={theme.primary}
                      color={theme.textOnPrimary}
                      _hover={{ bg: theme.primaryHover }}
                    >
                      Save Business Details
                    </Button>
                  </TabPanel>

                  <TabPanel>
                    <SectionTitle
                      title="Business Members"
                      description="Manage members stored in this account's existing members array."
                    />
                    <VStack align="stretch" spacing={4}>
                      {members.length === 0 && (
                        <EmptyState
                          text="This account has no members."
                          theme={theme}
                        />
                      )}
                      {members.map((member, index) => (
                        <Box
                          key={member.id || index}
                          p={4}
                          border="1px solid"
                          borderColor={borderColor}
                          borderRadius={cardsBorderRadii}
                          bg={softBg}
                        >
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                            <AdminInput
                              label="Name"
                              value={member.name || member.displayName}
                              onChange={(value) =>
                                setMembers((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? {
                                          ...item,
                                          name: value,
                                          displayName: value,
                                        }
                                      : item,
                                  ),
                                )
                              }
                              theme={theme}
                            />
                            <AdminInput
                              label="Email"
                              value={member.email}
                              onChange={(value) =>
                                setMembers((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, email: value }
                                      : item,
                                  ),
                                )
                              }
                              theme={theme}
                            />
                            <AdminInput
                              label="Member UID"
                              value={member.id}
                              onChange={() => undefined}
                              theme={theme}
                              isReadOnly
                            />
                            <FormControl>
                              <FormLabel color={subtleText}>
                                Member Role
                              </FormLabel>
                              <Select
                                value={normalizeUserRole(member.role)}
                                onChange={(event) =>
                                  setMembers((current) =>
                                    current.map((item, itemIndex) =>
                                      itemIndex === index
                                        ? { ...item, role: event.target.value }
                                        : item,
                                    ),
                                  )
                                }
                                bg={inputBg}
                                borderColor={inputBorder}
                              >
                                {ALL_USER_ROLES.filter(
                                  (role) => role !== USER_ROLES.SUPER_ADMIN,
                                ).map((role) => (
                                  <option key={role} value={role}>
                                    {formatUserRole(role)}
                                  </option>
                                ))}
                              </Select>
                            </FormControl>
                          </SimpleGrid>
                          <Flex
                            mt={3}
                            direction={{ base: "column", sm: "row" }}
                            justify="flex-end"
                            gap={2}
                          >
                            <Button
                              size="sm"
                              w={{ base: "100%", sm: "auto" }}
                              onClick={() => updateMember(members[index])}
                              isLoading={
                                savingSection === `member-${member.id}`
                              }
                            >
                              Save Member
                            </Button>
                            <IconButton
                              aria-label="Remove member"
                              icon={<Trash2 size={15} />}
                              size="sm"
                              w={{ base: "100%", sm: "auto" }}
                              colorScheme="red"
                              variant="outline"
                              onClick={() => removeMember(member.id)}
                              isLoading={
                                savingSection === `member-remove-${member.id}`
                              }
                            />
                          </Flex>
                        </Box>
                      ))}

                      <Divider />
                      <Text fontWeight="700">Add Member</Text>
                      <Text fontSize="sm" color={subtleText}>
                        Type a name, email address, or user ID. Select the user
                        to prefill their details, choose the member role, and
                        then save.
                      </Text>

                      <UserSearchDropdown onSelect={selectMemberForAdd} />

                      {newMember && (
                        <Box
                          p={4}
                          border="1px solid"
                          borderColor={borderColor}
                          borderRadius={cardsBorderRadii}
                          bg={softBg}
                        >
                          <Flex
                            gap={3}
                            align={{ base: "flex-start", md: "center" }}
                            direction={{ base: "column", md: "row" }}
                          >
                            <HStack flex="1" minW={0}>
                              <Avatar
                                size="md"
                                src={newMember.photoURL}
                                name={String(
                                  newMember.displayName ||
                                    newMember.name ||
                                    newMember.email ||
                                    newMember.id,
                                )}
                              />
                              <Box minW={0}>
                                <Text fontWeight="800" noOfLines={1}>
                                  {newMember.displayName ||
                                    newMember.name ||
                                    "Unnamed user"}
                                </Text>
                                <Text
                                  fontSize="sm"
                                  color={subtleText}
                                  noOfLines={1}
                                >
                                  {newMember.email || newMember.id}
                                </Text>
                                <Text
                                  fontSize="xs"
                                  color={subtleText}
                                  noOfLines={1}
                                >
                                  {newMember.id}
                                </Text>
                              </Box>
                            </HStack>

                            <FormControl maxW={{ base: "100%", md: "220px" }}>
                              <FormLabel color={subtleText}>
                                Member Role
                              </FormLabel>
                              <Select
                                value={normalizeUserRole(
                                  newMember.role || USER_ROLES.STAFF,
                                )}
                                onChange={(event) =>
                                  setNewMember((current) =>
                                    current
                                      ? { ...current, role: event.target.value }
                                      : current,
                                  )
                                }
                                bg={inputBg}
                                borderColor={inputBorder}
                              >
                                {ALL_USER_ROLES.filter(
                                  (role) => role !== USER_ROLES.SUPER_ADMIN,
                                ).map((role) => (
                                  <option key={role} value={role}>
                                    {formatUserRole(role)}
                                  </option>
                                ))}
                              </Select>
                            </FormControl>
                          </Flex>

                          <Flex
                            mt={4}
                            direction={{ base: "column-reverse", sm: "row" }}
                            justify="flex-end"
                            gap={2}
                          >
                            <Button
                              w={{ base: "100%", sm: "auto" }}
                              variant="ghost"
                              onClick={() => setNewMember(null)}
                              isDisabled={savingSection === "member-add"}
                            >
                              Cancel
                            </Button>
                            <Button
                              w={{ base: "100%", sm: "auto" }}
                              leftIcon={<Plus size={16} />}
                              onClick={addMember}
                              isLoading={savingSection === "member-add"}
                            >
                              Add Member
                            </Button>
                          </Flex>
                        </Box>
                      )}
                    </VStack>
                  </TabPanel>

                  <TabPanel>
                    <SectionTitle
                      title="Addresses"
                      description="Edit or remove addresses from the existing addresses array."
                    />
                    <VStack align="stretch" spacing={4}>
                      {addresses.length === 0 && (
                        <EmptyState
                          text="This account has no saved addresses."
                          theme={theme}
                        />
                      )}
                      {addresses.map((address, index) => (
                        <Box
                          key={address.id || index}
                          p={4}
                          border="1px solid"
                          borderColor={borderColor}
                          borderRadius={cardsBorderRadii}
                          bg={softBg}
                        >
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                            <AdminInput
                              label="Label"
                              value={address.label}
                              onChange={(value) =>
                                setAddresses((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, label: value }
                                      : item,
                                  ),
                                )
                              }
                              theme={theme}
                            />
                            <AdminInput
                              label="Address"
                              value={address.addressLine}
                              onChange={(value) =>
                                setAddresses((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, addressLine: value }
                                      : item,
                                  ),
                                )
                              }
                              theme={theme}
                            />
                            <AdminInput
                              label="Instructions"
                              value={address.instructions}
                              onChange={(value) =>
                                setAddresses((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, instructions: value }
                                      : item,
                                  ),
                                )
                              }
                              theme={theme}
                            />
                            <AdminInput
                              label="Latitude"
                              value={address.latitude}
                              onChange={(value) =>
                                setAddresses((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, latitude: toNumber(value) }
                                      : item,
                                  ),
                                )
                              }
                              theme={theme}
                              type="number"
                            />
                            <AdminInput
                              label="Longitude"
                              value={address.longitude}
                              onChange={(value) =>
                                setAddresses((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, longitude: toNumber(value) }
                                      : item,
                                  ),
                                )
                              }
                              theme={theme}
                              type="number"
                            />
                            <Flex
                              align="center"
                              pt={{ base: 1, md: 7 }}
                              gap={2}
                              flexWrap="wrap"
                            >
                              <Switch
                                isChecked={Boolean(address.isCurrent)}
                                onChange={(event) =>
                                  setAddresses((current) =>
                                    current.map((item, itemIndex) =>
                                      itemIndex === index
                                        ? {
                                            ...item,
                                            isCurrent: event.target.checked,
                                          }
                                        : item,
                                    ),
                                  )
                                }
                              />
                              <Text>Current</Text>
                              <Switch
                                isChecked={Boolean(address.isPickup)}
                                onChange={(event) =>
                                  setAddresses((current) =>
                                    current.map((item, itemIndex) =>
                                      itemIndex === index
                                        ? {
                                            ...item,
                                            isPickup: event.target.checked,
                                          }
                                        : item,
                                    ),
                                  )
                                }
                              />
                              <Text>Pickup</Text>
                            </Flex>
                          </SimpleGrid>
                          <Flex
                            mt={3}
                            direction={{ base: "column", sm: "row" }}
                            justify="flex-end"
                            gap={2}
                          >
                            <Button
                              size="sm"
                              w={{ base: "100%", sm: "auto" }}
                              onClick={() => saveAddress(addresses[index])}
                              isLoading={
                                savingSection === `address-${address.id}`
                              }
                            >
                              Save Address
                            </Button>
                            <IconButton
                              aria-label="Remove address"
                              icon={<Trash2 size={15} />}
                              size="sm"
                              w={{ base: "100%", sm: "auto" }}
                              colorScheme="red"
                              variant="outline"
                              onClick={() => removeAddress(address.id)}
                              isLoading={
                                savingSection === `address-remove-${address.id}`
                              }
                            />
                          </Flex>
                        </Box>
                      ))}
                    </VStack>
                  </TabPanel>

                  <TabPanel>
                    <SectionTitle
                      title="Warehouses"
                      description="Edit or remove warehouses from the existing warehouses array."
                    />
                    <VStack align="stretch" spacing={4}>
                      {warehouses.length === 0 && (
                        <EmptyState
                          text="This account has no warehouses."
                          theme={theme}
                        />
                      )}
                      {warehouses.map((warehouse, index) => (
                        <Box
                          key={warehouse.id || index}
                          p={4}
                          border="1px solid"
                          borderColor={borderColor}
                          borderRadius={cardsBorderRadii}
                          bg={softBg}
                        >
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                            <AdminInput
                              label="Name"
                              value={warehouse.name}
                              onChange={(value) =>
                                setWarehouses((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, name: value }
                                      : item,
                                  ),
                                )
                              }
                              theme={theme}
                            />
                            <AdminInput
                              label="Address"
                              value={warehouse.address}
                              onChange={(value) =>
                                setWarehouses((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, address: value }
                                      : item,
                                  ),
                                )
                              }
                              theme={theme}
                            />
                            <AdminInput
                              label="Description"
                              value={warehouse.description}
                              onChange={(value) =>
                                setWarehouses((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, description: value }
                                      : item,
                                  ),
                                )
                              }
                              theme={theme}
                            />
                            <AdminInput
                              label="WhatsApp Number"
                              value={warehouse.whatsappNumber}
                              onChange={(value) =>
                                setWarehouses((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, whatsappNumber: value }
                                      : item,
                                  ),
                                )
                              }
                              theme={theme}
                            />
                            <AdminInput
                              label="Latitude"
                              value={warehouse.lat}
                              onChange={(value) =>
                                setWarehouses((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, lat: toNumber(value) }
                                      : item,
                                  ),
                                )
                              }
                              theme={theme}
                              type="number"
                            />
                            <AdminInput
                              label="Longitude"
                              value={warehouse.lng}
                              onChange={(value) =>
                                setWarehouses((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, lng: toNumber(value) }
                                      : item,
                                  ),
                                )
                              }
                              theme={theme}
                              type="number"
                            />
                            <AdminInput
                              label="Photo URL"
                              value={warehouse.photoURL}
                              onChange={(value) =>
                                setWarehouses((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, photoURL: value }
                                      : item,
                                  ),
                                )
                              }
                              theme={theme}
                            />
                            <Flex
                              align="center"
                              pt={{ base: 1, md: 7 }}
                              gap={2}
                            >
                              <Switch
                                isChecked={Boolean(warehouse.isActive)}
                                onChange={(event) =>
                                  setWarehouses((current) =>
                                    current.map((item, itemIndex) =>
                                      itemIndex === index
                                        ? {
                                            ...item,
                                            isActive: event.target.checked,
                                          }
                                        : item,
                                    ),
                                  )
                                }
                              />
                              <Text>Active</Text>
                            </Flex>
                          </SimpleGrid>
                          <Flex
                            mt={3}
                            direction={{ base: "column", sm: "row" }}
                            justify="flex-end"
                            gap={2}
                          >
                            <Button
                              size="sm"
                              w={{ base: "100%", sm: "auto" }}
                              onClick={() => saveWarehouse(warehouses[index])}
                              isLoading={
                                savingSection === `warehouse-${warehouse.id}`
                              }
                            >
                              Save Warehouse
                            </Button>
                            <IconButton
                              aria-label="Remove warehouse"
                              icon={<Trash2 size={15} />}
                              size="sm"
                              w={{ base: "100%", sm: "auto" }}
                              colorScheme="red"
                              variant="outline"
                              onClick={() => removeWarehouse(warehouse.id)}
                              isLoading={
                                savingSection ===
                                `warehouse-remove-${warehouse.id}`
                              }
                            />
                          </Flex>
                        </Box>
                      ))}
                    </VStack>
                  </TabPanel>

                  <TabPanel>
                    <SectionTitle
                      title="Dengu Points and Security"
                      description="Adjust points through an audited transaction and perform protected account actions."
                    />
                    <VStack align="stretch" spacing={5}>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <Box
                          p={4}
                          border="1px solid"
                          borderColor={borderColor}
                          borderRadius={cardsBorderRadii}
                          bg={softBg}
                        >
                          <Text color={subtleText} fontSize="sm">
                            Current Dengu Points
                          </Text>
                          <Heading size="md">
                            {toNumber(
                              editForm.denguPoints ?? editForm.totalDenguPoints,
                            )}
                          </Heading>
                        </Box>
                        <Box
                          p={4}
                          border="1px solid"
                          borderColor={borderColor}
                          borderRadius={cardsBorderRadii}
                          bg={softBg}
                        >
                          <Text color={subtleText} fontSize="sm">
                            Authentication
                          </Text>
                          <Text fontWeight="700">
                            {editForm.emailVerified
                              ? "Email verified"
                              : "Email not verified"}
                          </Text>
                        </Box>
                      </SimpleGrid>

                      <FormControl>
                        <FormLabel color={subtleText}>
                          Points Adjustment
                        </FormLabel>
                        <NumberInput
                          value={pointsAmount}
                          onChange={(_, value) =>
                            setPointsAmount(Number.isFinite(value) ? value : 0)
                          }
                        >
                          <NumberInputField
                            bg={inputBg}
                            borderColor={inputBorder}
                            placeholder="Use a negative value to deduct"
                          />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel color={subtleText}>Points Reason</FormLabel>
                        <Textarea
                          value={pointsReason}
                          onChange={(event) =>
                            setPointsReason(event.target.value)
                          }
                          bg={inputBg}
                          borderColor={inputBorder}
                          placeholder="Reason for credit or deduction"
                        />
                      </FormControl>
                      <Button
                        w={{ base: "100%", sm: "auto" }}
                        alignSelf={{ base: "stretch", sm: "flex-start" }}
                        onClick={adjustPoints}
                        isLoading={savingSection === "points"}
                      >
                        Apply Points Adjustment
                      </Button>

                      <Divider />

                      <Flex direction={{ base: "column", sm: "row" }} gap={3}>
                        <Button
                          w={{ base: "100%", sm: "auto" }}
                          leftIcon={<KeyRound size={16} />}
                          onClick={generateResetLink}
                          isLoading={savingSection === "reset-link"}
                        >
                          Create Password Reset Link
                        </Button>
                        <Button
                          w={{ base: "100%", sm: "auto" }}
                          leftIcon={<Trash2 size={16} />}
                          colorScheme="red"
                          variant="outline"
                          onClick={deleteDialog.onOpen}
                          isDisabled={
                            selectedUser?.uid === currentFirebaseUser?.uid
                          }
                        >
                          Delete Account
                        </Button>
                      </Flex>

                      {resetLink && (
                        <Box
                          p={4}
                          border="1px solid"
                          borderColor={borderColor}
                          borderRadius={cardsBorderRadii}
                          bg={softBg}
                        >
                          <Text fontSize="sm" color={subtleText} mb={2}>
                            Password reset link
                          </Text>
                          <Flex
                            direction={{ base: "column", sm: "row" }}
                            gap={2}
                          >
                            <Input
                              value={resetLink}
                              isReadOnly
                              bg={inputBg}
                              borderColor={inputBorder}
                              minW={0}
                            />
                            <IconButton
                              aria-label="Copy reset link"
                              icon={<Copy size={16} />}
                              w={{ base: "100%", sm: "auto" }}
                              onClick={async () => {
                                await navigator.clipboard.writeText(resetLink);
                                toast({
                                  title: "Reset link copied",
                                  status: "success",
                                  duration: 1500,
                                });
                              }}
                            />
                          </Flex>
                        </Box>
                      )}
                    </VStack>
                  </TabPanel>

                  <TabPanel>
                    <SectionTitle
                      title="Administrative Audit History"
                      description="Review role, status, profile, member, points, and deletion actions for this user."
                    />
                    <Button
                      mb={4}
                      w={{ base: "100%", sm: "auto" }}
                      leftIcon={<RefreshCw size={16} />}
                      onClick={loadAuditLogs}
                      isLoading={savingSection === "audit"}
                    >
                      Refresh Audit History
                    </Button>
                    <VStack align="stretch" spacing={3}>
                      {auditLogs.length === 0 && (
                        <EmptyState
                          text="No audit records were found for this user."
                          theme={theme}
                        />
                      )}
                      {auditLogs.map((log) => (
                        <Box
                          key={log.id}
                          p={4}
                          border="1px solid"
                          borderColor={borderColor}
                          borderRadius={cardsBorderRadii}
                          bg={softBg}
                        >
                          <Flex
                            justify="space-between"
                            align="flex-start"
                            direction={{ base: "column", sm: "row" }}
                            gap={2}
                          >
                            <Box>
                              <Text fontWeight="700">{log.action}</Text>
                              <Text fontSize="sm" color={subtleText}>
                                {log.reason || "No reason supplied"}
                              </Text>
                            </Box>
                            <Text
                              fontSize="xs"
                              color={subtleText}
                              whiteSpace={{ base: "normal", sm: "nowrap" }}
                            >
                              {formatDate(log.createdAt)}
                            </Text>
                          </Flex>
                          <Text mt={2} fontSize="xs" color={subtleText}>
                            Performed by: {log.actorUid}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </DrawerBody>

          <DrawerFooter
            borderTop="1px solid"
            borderColor={borderColor}
            px={{ base: 4, md: 6 }}
          >
            <Button
              w={{ base: "100%", sm: "auto" }}
              variant="outline"
              onClick={detailsDrawer.onClose}
            >
              Close
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelDeleteRef}
        onClose={deleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent
            bg={cardBg}
            color={theme.text}
            mx={{ base: 3, sm: 0 }}
            maxW={{ base: "calc(100vw - 24px)", sm: "lg" }}
          >
            <AlertDialogHeader>Delete user account</AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={4}>
                Soft delete disables the Firebase account and retains records.
                Permanent deletion removes Authentication and the Firestore user
                document.
              </Text>
              <FormControl isRequired>
                <FormLabel>Deletion reason</FormLabel>
                <Textarea
                  value={deleteReason}
                  onChange={(event) => setDeleteReason(event.target.value)}
                  bg={inputBg}
                  borderColor={inputBorder}
                />
              </FormControl>
              <Flex mt={4} align="center" gap={2} flexWrap="wrap">
                <Switch
                  isChecked={permanentDelete}
                  onChange={(event) => setPermanentDelete(event.target.checked)}
                  colorScheme="red"
                />
                <Text>Permanent deletion</Text>
              </Flex>
            </AlertDialogBody>
            <AlertDialogFooter
              flexDirection={{ base: "column-reverse", sm: "row" }}
              gap={2}
            >
              <Button
                ref={cancelDeleteRef}
                w={{ base: "100%", sm: "auto" }}
                onClick={deleteDialog.onClose}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                ml={{ base: 0, sm: 3 }}
                w={{ base: "100%", sm: "auto" }}
                onClick={confirmDeleteUser}
                isLoading={savingSection === "delete"}
              >
                {permanentDelete ? "Delete permanently" : "Soft delete"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

function AdminInput({
  label,
  value,
  onChange,
  theme,
  type = "text",
  isReadOnly = false,
}: {
  label: string;
  value: unknown;
  onChange: (value: string) => void;
  theme: any;
  type?: string;
  isReadOnly?: boolean;
}) {
  return (
    <FormControl>
      <FormLabel color={theme.textMuted}>{label}</FormLabel>
      <Input
        type={type}
        value={toInputValue(value)}
        onChange={(event) => onChange(event.target.value)}
        isReadOnly={isReadOnly}
        bg={isReadOnly ? theme.surfaceAlt : theme.inputBg}
        color={theme.text}
        borderColor={isReadOnly ? theme.border : theme.inputBorder}
      />
    </FormControl>
  );
}

function SectionTitle({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <Box mb={5}>
      <Heading size="md">{title}</Heading>
      <Text mt={1} color="gray.500" fontSize="sm">
        {description}
      </Text>
    </Box>
  );
}

function EmptyState({ text, theme }: { text: string; theme: any }) {
  return (
    <Box
      p={5}
      border="1px dashed"
      borderColor={theme.border}
      borderRadius="8px"
      bg={theme.surfaceAlt}
    >
      <Text color={theme.textMuted}>{text}</Text>
    </Box>
  );
}

function SummaryTile({
  label,
  value,
  theme,
}: {
  label: string;
  value: number;
  theme: any;
}) {
  return (
    <Box
      minW={0}
      w="100%"
      p={3}
      border="1px solid"
      borderColor={theme.border}
      borderRadius="8px"
      bg={theme.surfaceAlt}
    >
      <Text fontSize="xs" color={theme.textMuted}>
        {label}
      </Text>
      <Text fontWeight="800" fontSize="lg">
        {value}
      </Text>
    </Box>
  );
}

export default UsersAdminPage;
